import type { EntryValueViewT } from "@patient-watch/types";

export interface MsAdlRatingsScaleOptions {
    min?: number;
    max?: number;
    step?: number;
    minActivities?: number;
    maxActivities?: number;
}

export interface BaselineEntryValueLookup {
    baselineEntryId: string | null;
    baselineJson: Record<string, number> | null;
    lockedActivities: string[];
    isFollowUp: boolean;
    isCurrentEntryBaseline: boolean;
}

export function parseMsAdlRatingsScaleOptions(scaleOptions: unknown): Required<MsAdlRatingsScaleOptions> {
    const first = Array.isArray(scaleOptions) ? scaleOptions[0] : null;
    const options = (first ?? {}) as MsAdlRatingsScaleOptions;
    return {
        min: options.min ?? 0,
        max: options.max ?? 10,
        step: options.step ?? 1,
        minActivities: options.minActivities ?? 1,
        maxActivities: options.maxActivities ?? 3,
    };
}

export function findBaselineEntryValue(
    entries: Array<{ entry_id: string; created_at?: string | null; entry_values_view?: EntryValueViewT[] | null }>,
    inputId: string
): { baselineEntryId: string | null; baselineJson: Record<string, number> | null } {
    const sortedEntries = [...entries].sort((a, b) =>
        String(a.created_at ?? "").localeCompare(String(b.created_at ?? ""))
    );

    for (const entry of sortedEntries) {
        const entryValue = entry.entry_values_view?.find(
            (value) =>
                value.input_id === inputId &&
                value.json_value &&
                typeof value.json_value === "object" &&
                !Array.isArray(value.json_value) &&
                Object.keys(value.json_value as Record<string, unknown>).length > 0
        );

        if (
            entryValue?.json_value &&
            typeof entryValue.json_value === "object" &&
            !Array.isArray(entryValue.json_value)
        ) {
            return {
                baselineEntryId: entry.entry_id,
                baselineJson: entryValue.json_value as Record<string, number>,
            };
        }
    }

    return { baselineEntryId: null, baselineJson: null };
}

export function resolveBaselineEntryValueState(
    entries: Array<{ entry_id: string; created_at?: string | null; entry_values_view?: EntryValueViewT[] | null }>,
    inputId: string,
    currentEntryId?: string
): BaselineEntryValueLookup {
    const { baselineEntryId, baselineJson } = findBaselineEntryValue(entries, inputId);
    const lockedActivities = baselineJson ? Object.keys(baselineJson) : [];
    const isCurrentEntryBaseline = !baselineEntryId || baselineEntryId === currentEntryId;
    const isFollowUp = Boolean(baselineEntryId && !isCurrentEntryBaseline);

    return {
        baselineEntryId,
        baselineJson,
        lockedActivities,
        isFollowUp,
        isCurrentEntryBaseline,
    };
}

export function buildFollowUpInitialValue(lockedActivities: string[], minRating: number): Record<string, number> {
    return Object.fromEntries(lockedActivities.map((activity) => [activity, minRating]));
}

export function toggleBaselineActivity(
    currentValue: Record<string, number>,
    activity: string,
    selected: boolean,
    maxActivities: number,
    defaultRating: number
): Record<string, number> {
    if (selected) {
        if (currentValue[activity] !== undefined || Object.keys(currentValue).length >= maxActivities) {
            return currentValue;
        }
        return { ...currentValue, [activity]: defaultRating };
    }

    const nextValue = { ...currentValue };
    delete nextValue[activity];
    return nextValue;
}

export function validateMsAdlRatingsValue(
    value: Record<string, number>,
    {
        minActivities,
        maxActivities,
        minRating,
        maxRating,
        allowedActivities,
        lockedActivities,
        isFollowUp,
    }: {
        minActivities: number;
        maxActivities: number;
        minRating: number;
        maxRating: number;
        allowedActivities?: string[] | null;
        lockedActivities?: string[];
        isFollowUp: boolean;
    }
): string | null {
    const keys = Object.keys(value);

    if (keys.length < minActivities || keys.length > maxActivities) {
        return `Select between ${minActivities} and ${maxActivities} activities of daily living`;
    }

    if (isFollowUp && lockedActivities) {
        const expected = [...lockedActivities].sort();
        const actual = [...keys].sort();
        if (expected.join("|") !== actual.join("|")) {
            return "Follow-up submissions must rate the same activities of daily living selected at baseline";
        }
    }

    for (const key of keys) {
        if (allowedActivities?.length && !allowedActivities.includes(key)) {
            return `"${key}" is not in the allowed activity list`;
        }
        const rating = value[key];
        if (typeof rating !== "number" || Number.isNaN(rating) || rating < minRating || rating > maxRating) {
            return `Rating for "${key}" must be between ${minRating} and ${maxRating}`;
        }
    }

    return null;
}
